/**
 * @module app.cm
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { UserService } from 'app/core/services/user-service';
import { ICaseSearchFilters, CaseService } from 'app/cm/services/case-service';
import { I18N } from 'aurelia-i18n';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { apiCall } from 'app/shared/lib/api/api';
import { IDisplayOptions } from 'app/shared/models/essette-interfaces';
import * as CM from 'app/cm/models/essette-api';
import * as Core from 'app/core/models/essette-api';

export interface ICaseSearchOptions {
    mine: IDisplayOptions;
    myCareTeam: IDisplayOptions;
    openOnly: IDisplayOptions;
    closedOnly: IDisplayOptions;
    assignedTo?: IDisplayOptions;
    careTeamParticipant?: IDisplayOptions;
}

@autoinject()
export class CaseFiltersCustomElement extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.object({ defaultBindingMode: bindingMode.twoWay })
    public searchFilters: ICaseSearchFilters = {};

    @bindable.objectNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'setOptions' })
    public options: ICaseSearchOptions | null = null

    private readonly defaultOptions: ICaseSearchOptions = {
        mine: { isVisible: false, isLocked: false },
        myCareTeam: { isVisible: false, isLocked: false },
        openOnly: { isVisible: true, isLocked: false },
        closedOnly: { isVisible: true, isLocked: false },
        assignedTo: { isVisible: true, isLocked: false },
        careTeamParticipant: { isVisible: false, isLocked: false }
    }

    protected toggleCount: number = 0;

    protected programGroups: CM.ProgramGroup[] = [];
    protected statuses: CM.CaseStatus[] = [];
    protected acuities: CM.Acuity[] = [];
    protected intensities: CM.Intensity[] = [];
    protected assigned: Core.User[] = [];
    protected careTeamParticipants: Core.User[] = [];

    protected maxCaseNumberLength: number = 20;

    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly userService: UserService,
        private readonly caseService: CaseService,
        private readonly i18n: I18N,
        ...rest: any[]) {

        super(...rest);
    }

    protected bind(): void {

        this.options = Object.assign<ICaseSearchOptions, Partial<ICaseSearchOptions>>(this.defaultOptions, this.options || {});

        if(this.options.mine.isVisible) { this.toggleCount += 1; }
        if(this.options.myCareTeam.isVisible) { this.toggleCount += 1; }
        if(this.options.openOnly.isVisible) { this.toggleCount += 1; }
        if(this.options.closedOnly.isVisible) { this.toggleCount += 1; }

        this.toggleMyCasesOnly(this.searchFilters.mine || false);
        this.toggleOpenCasesOnly(this.searchFilters.openOnly || false);
        this.toggleClosedCasesOnly(this.searchFilters.closedOnly || false);

        this.loadLookups();
    }


    ////****************** Events ***************************************************////


    ////****************** Functions ************************************************////
    protected setOptions(options: ICaseSearchOptions): void {
        this.options = Object.assign<ICaseSearchOptions, Partial<ICaseSearchOptions>>(this.defaultOptions, options || {});
    }

    protected toggleMyCasesOnly(value: boolean): void {
        this.searchFilters.mine = value;

        if(value) {
            const user = this.assigned.find(a => a.id === this.env.user.id);
            this.searchFilters.assignedTo = user ?? undefined;
        }
        else {
            this.searchFilters.assignedTo = undefined;
        }
    }

    protected toggleMyCareTeamCases(value: boolean): void {
        this.searchFilters.myCareTeam = value;

        if(value) {
            const user = this.careTeamParticipants.find(a => a.id === this.env.user.id);
            this.searchFilters.careTeamParticipant = user ?? undefined;
        }
        else {
            this.searchFilters.careTeamParticipant = undefined;
        }
    }

    protected toggleOpenCasesOnly(value: boolean): void {
        this.searchFilters.openOnly = value;

        if(value) {
            this.searchFilters.status = undefined;
        }
    }

    protected toggleClosedCasesOnly(value: boolean): void {
        this.searchFilters.closedOnly = value;

        if(value) {
            this.searchFilters.status = undefined;
        }
    }

    protected programGroupChange(): void {
        this.searchFilters.program = null;
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadLookups(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            all: true
        };

        const programGroupIncludes = ['programs'];
        const programGroupFields = ['description', 'programs.description'];
        const fields = ['description'];

        try {
            this.state.beginLoad();
            const [programGroups, statuses, acuities, intensities, users, careTeamParticipants] = await Promise.all(
                [
                    this.caseService.loadProgramGroups(options, programGroupIncludes, true, undefined, undefined, programGroupFields),
                    this.caseService.loadCaseStatuses(options, undefined, undefined, fields),
                    this.caseService.loadAcuities(options, undefined, fields),
                    this.caseService.loadIntensities(options, undefined, fields),
                    this.options?.assignedTo?.isVisible ? this.caseService.loadCaseOwners(options, undefined, undefined, undefined, fields) : null,
                    this.options?.careTeamParticipant?.isVisible ? this.userService.loadUsers(options, undefined, undefined, fields) : null
                ]
            );

            this.programGroups = programGroups.results;
            this.statuses = statuses.results;
            this.acuities = acuities.results;
            this.intensities = intensities.results;
            this.assigned = users?.results || [];
            this.careTeamParticipants = careTeamParticipants?.results || [];

            // add empty records to represent unassigned values
            this.assigned.unshift({
                id: '',
                $display: `<${this.i18n.tr('cm:case.assignedTo.none')}>`
            });

            this.programGroups.unshift({
                id: '',
                $display: `<${this.i18n.tr('ui:placeholders.select.none', { what: this.i18n.tr('cm:programGroup.$') })}>`
            });

            this.acuities.unshift({
                id: '',
                $display: `<${this.i18n.tr('ui:placeholders.select.none', { what: this.i18n.tr('cm:acuity.$') })}>`
            });

            this.intensities.unshift({
                id: '',
                $display: `<${this.i18n.tr('ui:placeholders.select.none', { what: this.i18n.tr('cm:intensity.$') })}>`
            });

            if(this.searchFilters.mine && this.env.user) {
                const user = this.assigned.find(a => a.id === this.env.user.id);
                if(user) { this.searchFilters.assignedTo = user; }
            }
        }
        catch(error) {
            this.logger.error('Error Loading Lookups', error);
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

}
